/**
 * Plan ↔ Stripe Price ID mapping.
 *
 * Each plan's Price ID comes from the Stripe dashboard → Products → Prices.
 * We map these through env vars so the same code runs in test and live mode
 * — swap STRIPE_PRICE_* values in .env.local (test) vs production env (live).
 *
 * To get Price IDs: create the 5 Products in Stripe Dashboard → Products.
 * Each Product has one or more Prices; each Price has an ID `price_...`.
 * See SETUP_STRIPE.md for the walk-through.
 */

export type PlanKey =
  | "pass"            // €39 one-time, 30-day access
  | "pro_monthly"     // €49/mo recurring
  | "pro_annual"      // €490/yr recurring (two months free)
  | "studio_monthly"  // €199/mo recurring
  | "studio_annual";  // €1,990/yr recurring (two months free)

export interface PlanDefinition {
  key: PlanKey;
  label: string;
  /** Stripe mode — 'payment' for one-time, 'subscription' for recurring. */
  mode: "payment" | "subscription";
  /** Internal DB enum value (matches subscription_plan in Supabase). */
  dbValue:
    | "pass_30d"
    | "pro_monthly" | "pro_annual"
    | "studio_monthly" | "studio_annual";
  /** Which env var holds the Stripe Price ID for this plan. */
  priceEnvVar: string;
  /**
   * For one-time purchases, the validity window (days) granted after payment.
   * Subscriptions ignore this — they use Stripe's current_period_end.
   */
  validityDays?: number;
}

export const PLANS: Record<PlanKey, PlanDefinition> = {
  pass: {
    key: "pass",
    label: "Buyer's Pass",
    mode: "payment",
    dbValue: "pass_30d",
    priceEnvVar: "STRIPE_PRICE_PASS_30D",
    validityDays: 30,
  },
  pro_monthly: {
    key: "pro_monthly",
    label: "Pro (monthly)",
    mode: "subscription",
    dbValue: "pro_monthly",
    priceEnvVar: "STRIPE_PRICE_PRO_MONTHLY",
  },
  pro_annual: {
    key: "pro_annual",
    label: "Pro (annual)",
    mode: "subscription",
    dbValue: "pro_annual",
    priceEnvVar: "STRIPE_PRICE_PRO_ANNUAL",
  },
  studio_monthly: {
    key: "studio_monthly",
    label: "Studio (monthly)",
    mode: "subscription",
    dbValue: "studio_monthly",
    priceEnvVar: "STRIPE_PRICE_STUDIO_MONTHLY",
  },
  studio_annual: {
    key: "studio_annual",
    label: "Studio (annual)",
    mode: "subscription",
    dbValue: "studio_annual",
    priceEnvVar: "STRIPE_PRICE_STUDIO_ANNUAL",
  },
};

export function getPriceId(plan: PlanKey): string | null {
  return process.env[PLANS[plan].priceEnvVar] ?? null;
}

/**
 * Priority ranking for resolving "best active plan" when a user has
 * overlapping subscriptions (e.g. Pass active + Pro just activated).
 * Higher number wins.
 */
export const DB_PLAN_RANK: Record<PlanDefinition["dbValue"], number> = {
  pass_30d: 1,
  pro_monthly: 2,
  pro_annual: 2,      // same tier as monthly
  studio_monthly: 3,
  studio_annual: 3,
};

export function isProOrBetter(dbPlan: PlanDefinition["dbValue"]): boolean {
  return DB_PLAN_RANK[dbPlan] >= 2;
}

export function isStudio(dbPlan: PlanDefinition["dbValue"]): boolean {
  return DB_PLAN_RANK[dbPlan] >= 3;
}

/**
 * The four user-facing plan tiers — what the UI distinguishes between.
 * (Pro/Studio collapse monthly+annual; Pass is its own tier; free is the
 * default for anyone without an active subscription row.)
 *
 * Named ResolvedPlanTier (not "ResolvedPlan") to avoid colliding with the
 * hook's exported `ResolvedPlan` which adds `null` for "not signed in".
 */
export type ResolvedPlanTier = "free" | "pass" | "pro" | "studio";

/**
 * Collapse a stored DB plan enum into a user-facing tier. Single source
 * of truth for both the client (useEntitlement) and server (entitlement
 * check API) so they cannot diverge.
 *
 * Unknown plan values fall back to "free" — never throw, since the DB
 * may legitimately add a new plan before the client knows about it.
 */
export function dbPlanToResolved(dbPlan: string | null | undefined): ResolvedPlanTier {
  if (dbPlan === "pass_30d") return "pass";
  if (dbPlan === "pro_monthly" || dbPlan === "pro_annual") return "pro";
  if (dbPlan === "studio_monthly" || dbPlan === "studio_annual") return "studio";
  return "free";
}
